export interface ChartDataPoint {
  label: string
  value: number
}

export interface ChartCardDef {
  key: string
  label: string
  category: string
  chartType: 'line' | 'bar' | 'donut'
  endpoint: string
  title: string
  valueFormatter?: (v: number) => string
  /** Transforms raw API response into chart data points */
  transform: (data: unknown) => ChartDataPoint[]
  defaultW: number
  defaultH: number
}

function fmtMonth(m: string): string {
  const [y, mo] = m.split('-')
  const d = new Date(Number(y), Number(mo) - 1, 1)
  return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })
}

function fmtEur(v: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

const PHASE_LABELS: Record<string, string> = {
  lead: 'Lead',
  qualified: 'Qualifiziert',
  proposal: 'Angebot',
  negotiation: 'Verhandlung',
  won: 'Gewonnen',
}

export const CHART_CARD_REGISTRY: ChartCardDef[] = [
  {
    key: 'chart.mrr-trend',
    label: 'MRR-Trend (12 Monate)',
    category: 'Diagramme',
    chartType: 'line',
    endpoint: '/reports/mrr?months=12',
    title: 'MRR / ARR Trend',
    valueFormatter: fmtEur,
    transform: (data) => {
      const trend = (data as { trend?: Array<{ month: string; mrrCents: number }> })?.trend ?? []
      return trend.map((p) => ({
        label: fmtMonth(p.month),
        value: Math.round(p.mrrCents / 100),
      }))
    },
    defaultW: 6,
    defaultH: 5,
  },
  {
    key: 'chart.margin-trend',
    label: 'Margen-Trend (12 Monate)',
    category: 'Diagramme',
    chartType: 'bar',
    endpoint: '/stats/margin-trend?months=12',
    title: 'Margen-Trend',
    valueFormatter: (v) => `${v} %`,
    transform: (data) => {
      const points = (data as { points?: Array<{ month: string; avgMarginPct: number }> })?.points ?? []
      return points.map((p) => ({
        label: fmtMonth(p.month),
        value: Math.round(p.avgMarginPct * 10) / 10,
      }))
    },
    defaultW: 6,
    defaultH: 5,
  },
  {
    key: 'chart.deal-pipeline',
    label: 'Deal-Pipeline (Phasen)',
    category: 'Diagramme',
    chartType: 'donut',
    endpoint: '/deals',
    title: 'Deal-Pipeline',
    valueFormatter: (v) => `${v} Deals`,
    transform: (data) => {
      const deals = Array.isArray(data) ? (data as Array<{ phase?: string }>) : []
      const counts: Record<string, number> = {}
      for (const d of deals) {
        const p = d.phase ?? 'unknown'
        if (p === 'unknown') continue
        counts[p] = (counts[p] ?? 0) + 1
      }
      return Object.entries(counts)
        .map(([k, v]) => ({ label: PHASE_LABELS[k] ?? k, value: v }))
        .sort((a, b) => b.value - a.value)
    },
    defaultW: 4,
    defaultH: 5,
  },
]

export const CHART_CARD_CATEGORIES = [...new Set(CHART_CARD_REGISTRY.map((c) => c.category))]

export function getChartCardDef(key: string): ChartCardDef | undefined {
  return CHART_CARD_REGISTRY.find((c) => c.key === key)
}
